import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
	Dialog,
	DialogBackdrop,
	DialogPanel,
	DialogTitle,
} from "@headlessui/react";
import { createCampaign, fetchCampaignById, updateCampaign, uploadCampainBanner } from "../../services/CampaignService";
import { Button } from "../../components/Button";
import ToggleSwitch from "../../components/ToggleSwitch";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { GoogleMap, useJsApiLoader, Marker, Autocomplete } from "@react-google-maps/api";
import config from "../../config";
import { showSuccessToast, showErrorToast } from "../../lib/ToastUtility";
import { FiEdit2 } from "react-icons/fi";

const GOOGLE_MAPS_API_KEY = config.googleMapsApiKey;

const GOOGLE_MAPS_LIBRARIES: ("places" | "drawing" | "geometry")[] = ["places"];

const mapContainerStyle = {
	width: "100%",
	height: "256px",
	borderRadius: "0.5rem",
};

const defaultCenter = { lat: 33.7701, lng: -118.1937 }; // Long Beach, CA

const NewCampaignCreateModel: React.FC<{
	open: boolean;
	onClose: () => void;
	campaignId?: string;
}> = ({
	open,
	onClose,
	campaignId,
}) => {
	const { control, handleSubmit, register, reset, setValue } = useForm();
	const [isActive, setIsActive] = useState(false);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [dragActive, setDragActive] = useState(false);
	const [description, setDescription] = useState("");
	const [loading, setLoading] = useState(false);
	const [imageUploading, setImageUploading] = useState(false);
	const [banner, setBanner] = useState<string | null>(null);

	const { isLoaded } = useJsApiLoader({
		googleMapsApiKey: GOOGLE_MAPS_API_KEY,
		libraries: GOOGLE_MAPS_LIBRARIES,
	});

	const [map, setMap] = useState<google.maps.Map | null>(null);
	const [markerPosition, setMarkerPosition] = useState<google.maps.LatLngLiteral | null>(defaultCenter);
	const [location, setLocation] = useState<string>("Long Beach, CA, USA");
	const [placeDetails, setPlaceDetails] = useState<any>(null);
	const autocompleteRef = React.useRef<google.maps.places.Autocomplete | null>(null);

	const onLoad = (mapInstance: google.maps.Map) => setMap(mapInstance);

	const onAutocompleteLoad = (autocomplete: google.maps.places.Autocomplete) => {
		autocompleteRef.current = autocomplete;
	};

	const onPlaceChanged = () => {
		const place = autocompleteRef.current?.getPlace();
		if (place && place.geometry && place.geometry.location) {
			const lat = place.geometry.location.lat();
			const lng = place.geometry.location.lng();
			setMarkerPosition({ lat, lng });
			setLocation(place.formatted_address || "");
			setPlaceDetails(place);
			map?.panTo({ lat, lng });
		}
	};

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files[0]) {
			setSelectedFile(e.target.files[0]);
			setImageUploading(true);
			const formData = new FormData();
			formData.append('image', e.target.files[0]);
			const res = await uploadCampainBanner(formData);
			if (res?.data?.url) {
				setBanner(res.data.url);
			}
			setImageUploading(false);
		}
	};

	const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(true);
	};

	const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);
	};

	const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);
		if (e.dataTransfer.files && e.dataTransfer.files[0]) {
			setSelectedFile(e.dataTransfer.files[0]);
		}
	};

	const onSubmit = async (data: any) => {
		const formData = {
			...data,
			description: description,
			slug: "",
			banner: banner || "",
			country: null,
			city: null,
			locationText: location,
			addressText: location,
			lattitude: markerPosition?.lat?.toString() || "",
			longtitude: markerPosition?.lng?.toString() || "",
			targetAmount: +data.targetAmount || 0,
			amountCollected: 0,
			startDate: new Date(),
			endDate: null,
			isActive: isActive,
			images: null,
			videos: null,
			supplierId: "",
			donationAidUnits: 0,
			supplierCode: null,
			completed: false,
			campaignLabel: "",
			languageCode: "en"
		};

		try {
			if (campaignId) {
				await updateCampaign(campaignId, formData);
				showSuccessToast('Campaign updated successfully!');
			} else {
				await createCampaign(formData);
				showSuccessToast('Campaign created successfully!');
			}
			onClose();
		} catch (error) {
			showErrorToast('Failed to save campaign. Please try again.');
		}
	};

	useEffect(() => {
		const fetchCampaign = async () => {
			if (campaignId) {
				setLoading(true);
				try {
					const response = await fetchCampaignById(campaignId);
					if (response?.status !== 200) {
						showErrorToast("Failed to load campaign data.");
						return;
					}

					reset({
						name: response.data.name || "",
						targetAmount: response.data.targetAmount || "",
					});
					setDescription(response.data.description || "");
					setIsActive(response.data.isActive || false);
					setLocation(response.data.locationText || "");
					setMarkerPosition({
						lat: parseFloat(response.data.lattitude) || defaultCenter.lat,
						lng: parseFloat(response.data.longtitude) || defaultCenter.lng,
					 });
					setBanner(response.data.banner || null);
				} catch (e) {
					showErrorToast("Failed to load campaign data.");
				}
				setLoading(false);
			} else {
				reset();
				setSelectedFile(null);
				setDragActive(false);
				setDescription("");
				setIsActive(false);
				setMarkerPosition(defaultCenter);
				setLocation("Long Beach, CA, USA");
				setPlaceDetails(null);
				setBanner(null);
			}
		};
		if (open) fetchCampaign();
	}, [campaignId, open, reset]);

	return (
		<Dialog open={open} onClose={onClose} className="relative z-10">
			<DialogBackdrop className="fixed inset-0 bg-black/50 transition-opacity" />
			<div className="fixed inset-0 z-10 overflow-y-auto">
				<div className="flex min-h-full items-center justify-center p-4 text-center">
					<DialogPanel className="relative bg-white dark:bg-slate-900 rounded-2xl border border-white/50 dark:border-white/10 shadow-xl w-full max-w-3xl">
						<DialogTitle className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-b dark:border-white/10 px-6 py-4 text-left">
							{campaignId ? "Edit Campaign" : "New Campaign"}
						</DialogTitle>
						{loading ? (
							<div className="p-6 text-gray-900 dark:text-gray-100">Loading...</div>
						) : (
						<form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left">
									Campaign cover image
								</label>
									{imageUploading ? (
										<div className="flex items-center justify-center mt-2 h-48">
											<div className="flex flex-col items-center">
												<svg
													className="animate-spin h-8 w-8 text-primary-100 mb-2"
													xmlns="http://www.w3.org/2000/svg"
													fill="none"
													viewBox="0 0 24 24"
												>
													<circle
														className="opacity-25"
														cx="12"
														cy="12"
														r="10"
														stroke="currentColor"
														strokeWidth="4"
													></circle>
													<path
														className="opacity-75"
														fill="currentColor"
														d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
													></path>
												</svg>
												<span className="text-primary-100 text-sm">Uploading image...</span>
											</div>
										</div>
									) : banner ? (
										<div className="relative mt-2 w-full max-w-xs mx-auto flex items-start justify-center">
											<img
												src={banner}
												alt="Campaign Banner"
												className="rounded-lg border dark:border-white/10 bg-white dark:bg-slate-800"
												style={{ maxWidth: "100%", height: "auto", display: "block" }}
											/>
											<button
												type="button"
												className="absolute top-2 right-2 bg-white dark:bg-slate-800 bg-opacity-80 rounded-full p-1 shadow hover:bg-opacity-100 transition"
												onClick={() => {
													setBanner(null);
													setSelectedFile(null);
												}}
												title="Change image"
											>
												<FiEdit2 className="h-5 w-5 text-gray-700 dark:text-gray-300" />
											</button>
										</div>
									) : (
										<div
											className={`mt-2 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
												dragActive
													? "border-blue-400 bg-blue-50 dark:bg-blue-900/20"
													: "border-gray-300 dark:border-white/10"
											}`}
											onDragOver={handleDragOver}
											onDragLeave={handleDragLeave}
											onDrop={handleDrop}
											onClick={() => document.getElementById("campaign-image-input")?.click()}
										>
											<input
												id="campaign-image-input"
												type="file"
												accept="image/*"
												className="hidden"
												onChange={handleFileChange}
											/>
											{selectedFile ? (
												<span className="text-gray-700 dark:text-gray-300">{selectedFile.name}</span>
											) : (
												<span className="text-gray-500 dark:text-gray-400">
													Select file to upload or Drag and Drop here
												</span>
											)}
										</div>
									)}
							</div>

							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left">
									Campaign title
								</label>
								<input
									className="w-full mt-2 border border-gray-300 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 rounded-md p-2 focus:ring-primary-100 focus:border-primary-100"
									type="text"
									placeholder="Campaign name"
									{...register("name")}
								/>
							</div>

							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left">
									Campaign goal (bars)
								</label>
								<input
									className="w-full mt-2 border border-gray-300 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 rounded-md p-2 focus:ring-primary-100 focus:border-primary-100"
									type="number"
									placeholder="Enter a campaign goal in bars"
									{...register("targetAmount")}
								/>
							</div>

							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left">
									Campaign description
								</label>
								<div className="w-full mt-2 bg-white dark:bg-slate-800">
									<ReactQuill
										value={description}
										onChange={setDescription}
										theme="snow"
										modules={{
											toolbar: {
												container: [
													[{ header: [1, 2, false] }],
													["bold", "italic", "underline", "strike"],
													[{ list: "ordered" }, { list: "bullet" }],
													["link", "image"],
													["clean"],
												],
											},
										}}
									/>
								</div>
								<style>
								{`
									.ql-toolbar.ql-snow {
										justify-content: flex-start;
										display: flex;
									}
									.dark .ql-toolbar.ql-snow {
										background-color: #1e293b;
										border-color: rgba(255,255,255,0.1);
										color: #f1f5f9;
									}
									.dark .ql-toolbar.ql-snow .ql-stroke {
										stroke: #94a3b8;
									}
									.dark .ql-toolbar.ql-snow .ql-fill {
										fill: #94a3b8;
									}
									.dark .ql-toolbar.ql-snow .ql-picker {
										color: #94a3b8;
									}
									.dark .ql-toolbar.ql-snow .ql-picker-options {
										background-color: #1e293b;
										border-color: rgba(255,255,255,0.1);
									}
									.dark .ql-container.ql-snow {
										background-color: #1e293b;
										border-color: rgba(255,255,255,0.1);
										color: #f1f5f9;
									}
								`}
								</style>
							</div>

							<div className="flex items-center space-x-4">
								<ToggleSwitch
									rtext="Active campaign"
									status={isActive}
									setToggle={(val) => setIsActive(val)}
									id="campaign-active"
								/>
							</div>

							<div className="mt-4">
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left mb-1">
									Location
								</label>
								<div className="flex mb-2">
									{isLoaded && (
										<Autocomplete
											onLoad={onAutocompleteLoad}
											onPlaceChanged={onPlaceChanged}
											className="flex-1"
										>
											<input
												type="text"
												placeholder="Search location"
												className="border dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 rounded-l px-3 py-2 w-full"
												value={location}
												onChange={e => setLocation(e.target.value)}
											/>
										</Autocomplete>
									)}
								</div>
								{isLoaded ? (
									<GoogleMap
										mapContainerStyle={mapContainerStyle}
										center={markerPosition || defaultCenter}
										zoom={13}
										onLoad={onLoad}
									>
										{markerPosition && <Marker position={markerPosition} />}
									</GoogleMap>
								) : (
									<div className="h-64 w-full flex items-center justify-center rounded-lg bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-gray-400">
										Loading map...
									</div>
								)}
							</div>

							<div className="flex justify-end space-x-4">
								<Button
									variant="secondary"
									size="md"
									onClick={onClose}
								>
									Cancel
								</Button>
								<Button
									variant="primary"
									size="md"
									type="submit"
								>
									Confirm
								</Button>
							</div>
						</form>
						)}
					</DialogPanel>
				</div>
			</div>
		</Dialog>
	);
};

export default NewCampaignCreateModel;
